import {
	EASTER_EVENTIUM_PER_PICKUP,
	EASTER_EVENTIUM_PER_RARE_PICKUP,
	EASTER_MAX_SPAWN_DISTANCE,
	EASTER_PICKUP_CAP,
	EASTER_RARE_PICKUP_CHANCE,
	HALLOWEEN_EVENTIUM_PER_PICKUP,
	HALLOWEEN_EVENTIUM_PER_RARE_PICKUP,
	HALLOWEEN_MAX_SPAWN_DISTANCE,
	HALLOWEEN_OFFLINE_PICKUP_INTERVAL_SECONDS,
	HALLOWEEN_PICKUP_CAP,
	HALLOWEEN_RARE_PICKUP_CHANCE,
	HALLOWEEN_SPAWN_CHANCE,
	HALLOWEEN_STARTING_PICKUP_COUNT,
	PIRATES_EVENTIUM_PER_PICKUP,
	PIRATES_EVENTIUM_PER_RARE_PICKUP,
	PIRATES_MAX_SPAWN_DISTANCE,
	PIRATES_OFFLINE_PICKUP_INTERVAL_SECONDS,
	PIRATES_PICKUP_CAP,
	PIRATES_RARE_PICKUP_CHANCE,
	PIRATES_SPAWN_CHANCE,
	PIRATES_STARTING_PICKUP_COUNT,
} from "@/config/constants";
import { EventUpgradeIds } from "@/game/entities/config/upgrades";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import { TileLabelSkin } from "@/types/tile";
import { EventCategory } from "@/types/ui";

export enum CollectableType {
	COMMON = 0,
	RARE = 1,
}

export type EventCollectableConfig = {
	initialPickupCount: number;
	spawnChancePerTick: number;
	offlinePickupIntervalSeconds: number | null;
	getPickupCap: () => number;
	getRareChance: () => number;
	getMaxSpawnDistance: () => number;
	getReward: (type: CollectableType) => number;
	getPassiveIncome: () => number;
	getSkin: (type: CollectableType) => TileLabelSkin;
};

export type CollectableEventCategory =
	| EventCategory.EASTER
	| EventCategory.HALLOWEEN
	| EventCategory.PIRATES;

const getUpgradeValuesTotal = (ids: ReadonlyArray<EventUpgradeIds>): number =>
	ids.reduce(
		(total: number, id: EventUpgradeIds): number =>
			total + getUpgradeCurrentReplaceValueById(id),
		0,
	);

const easterConfig: EventCollectableConfig = {
	initialPickupCount: 0,
	spawnChancePerTick: 1,
	offlinePickupIntervalSeconds: null,
	getPickupCap: (): number =>
		EASTER_PICKUP_CAP +
		getUpgradeValuesTotal([
			EventUpgradeIds.ea_easter_bunny,
			EventUpgradeIds.ea_legendary_easter_bunny,
		]),
	getRareChance: (): number =>
		EASTER_RARE_PICKUP_CHANCE +
		getUpgradeValuesTotal([
			EventUpgradeIds.ea_golden_chance,
			EventUpgradeIds.ea_golden_jackpot,
			EventUpgradeIds.ea_eastertide_special2,
		]),
	getMaxSpawnDistance: (): number =>
		EASTER_MAX_SPAWN_DISTANCE -
		getUpgradeCurrentReplaceValueById(EventUpgradeIds.ea_egg_magnet),
	getReward: (type: CollectableType): number =>
		type === CollectableType.RARE
			? EASTER_EVENTIUM_PER_RARE_PICKUP +
				getUpgradeValuesTotal([
					EventUpgradeIds.ea_golden_surprise,
					EventUpgradeIds.ea_golden_surprise2,
					EventUpgradeIds.ea_golden_surprise3,
					EventUpgradeIds.ea_eastertide_special4,
				])
			: EASTER_EVENTIUM_PER_PICKUP +
				getUpgradeValuesTotal([
					EventUpgradeIds.ea_small_surprise,
					EventUpgradeIds.ea_medium_surprise,
					EventUpgradeIds.ea_big_surprise,
					EventUpgradeIds.ea_giant_surprise,
					EventUpgradeIds.ea_eastertide_special1,
				]),
	getPassiveIncome: (): number =>
		getUpgradeValuesTotal([
			EventUpgradeIds.ea_egg_painting,
			EventUpgradeIds.ea_trained_decorators,
			EventUpgradeIds.ea_artistic_decorators,
			EventUpgradeIds.ea_easter_workshops,
			EventUpgradeIds.ea_eventium_income,
			EventUpgradeIds.ea_eventium_income2,
			EventUpgradeIds.ea_eventium_income3,
			EventUpgradeIds.ea_eventium_income4,
			EventUpgradeIds.ea_eventium_income5,
		]),
	getSkin: (type: CollectableType): TileLabelSkin =>
		type === CollectableType.RARE
			? TileLabelSkin.EASTER_EGG_GOLDEN
			: TileLabelSkin.EASTER_EGG,
};

const halloweenConfig: EventCollectableConfig = {
	initialPickupCount: HALLOWEEN_STARTING_PICKUP_COUNT,
	spawnChancePerTick: HALLOWEEN_SPAWN_CHANCE,
	offlinePickupIntervalSeconds: HALLOWEEN_OFFLINE_PICKUP_INTERVAL_SECONDS,
	getPickupCap: (): number =>
		HALLOWEEN_PICKUP_CAP +
		getUpgradeValuesTotal([
			EventUpgradeIds.ha_pumpkin_seeds,
			EventUpgradeIds.ha_pumpkin_patch,
		]),
	getRareChance: (): number =>
		HALLOWEEN_RARE_PICKUP_CHANCE +
		getUpgradeValuesTotal([
			EventUpgradeIds.ha_will_o_the_wisp,
			EventUpgradeIds.ha_ignis_fatuus,
			EventUpgradeIds.ha_special2,
		]),
	getMaxSpawnDistance: (): number =>
		HALLOWEEN_MAX_SPAWN_DISTANCE -
		getUpgradeCurrentReplaceValueById(EventUpgradeIds.ha_pumpkin_magnet),
	getReward: (type: CollectableType): number =>
		type === CollectableType.RARE
			? HALLOWEEN_EVENTIUM_PER_RARE_PICKUP +
				getUpgradeValuesTotal([
					EventUpgradeIds.ha_mischievous_ghost,
					EventUpgradeIds.ha_troubled_ghost,
					EventUpgradeIds.ha_evil_ghost,
					EventUpgradeIds.ha_special4,
				])
			: HALLOWEEN_EVENTIUM_PER_PICKUP +
				getUpgradeValuesTotal([
					EventUpgradeIds.ha_small_pumpkin,
					EventUpgradeIds.ha_medium_pumpkin,
					EventUpgradeIds.ha_large_pumpkin,
					EventUpgradeIds.ha_giant_pumpkin,
					EventUpgradeIds.ha_special1,
				]),
	getPassiveIncome: (): number =>
		getUpgradeValuesTotal([
			EventUpgradeIds.ha_pumpkin_carving,
			EventUpgradeIds.ha_trained_carvers,
			EventUpgradeIds.ha_artistic_carvers,
			EventUpgradeIds.ha_halloween_workshops,
			EventUpgradeIds.ha_fire1,
			EventUpgradeIds.ha_fire2,
			EventUpgradeIds.ha_fire3,
			EventUpgradeIds.ha_fire4,
		]),
	getSkin: (type: CollectableType): TileLabelSkin =>
		type === CollectableType.RARE
			? TileLabelSkin.HALLOWEEN_JACK_O_LANTERN
			: TileLabelSkin.HALLOWEEN_PUMPKIN,
};

const piratesConfig: EventCollectableConfig = {
	initialPickupCount: PIRATES_STARTING_PICKUP_COUNT,
	spawnChancePerTick: PIRATES_SPAWN_CHANCE,
	offlinePickupIntervalSeconds: PIRATES_OFFLINE_PICKUP_INTERVAL_SECONDS,
	getPickupCap: (): number =>
		PIRATES_PICKUP_CAP +
		getUpgradeValuesTotal([
			EventUpgradeIds.pi_pirate_code,
			EventUpgradeIds.pi_rum_barrels,
		]),
	getRareChance: (): number =>
		PIRATES_RARE_PICKUP_CHANCE +
		getUpgradeValuesTotal([
			EventUpgradeIds.pi_treasure_hunters,
			EventUpgradeIds.pi_experienced_treasure_hunters,
			EventUpgradeIds.pi_pack2,
		]),
	getMaxSpawnDistance: (): number =>
		PIRATES_MAX_SPAWN_DISTANCE -
		getUpgradeCurrentReplaceValueById(EventUpgradeIds.pi_magnetic),
	getReward: (type: CollectableType): number =>
		type === CollectableType.RARE
			? PIRATES_EVENTIUM_PER_RARE_PICKUP +
				getUpgradeValuesTotal([
					EventUpgradeIds.pi_buried_treasure,
					EventUpgradeIds.pi_lost_treasure,
					EventUpgradeIds.pi_cursed_treasure,
					EventUpgradeIds.pi_pack4,
				])
			: PIRATES_EVENTIUM_PER_PICKUP +
				getUpgradeValuesTotal([
					EventUpgradeIds.pi_town_maps,
					EventUpgradeIds.pi_terrain_maps,
					EventUpgradeIds.pi_detailed_maps,
					EventUpgradeIds.pi_atlas,
					EventUpgradeIds.pi_pack1,
				]),
	getPassiveIncome: (): number =>
		getUpgradeValuesTotal([
			EventUpgradeIds.pi_coin_minting,
			EventUpgradeIds.pi_small_mint,
			EventUpgradeIds.pi_medium_mint,
			EventUpgradeIds.pi_large_mint,
			EventUpgradeIds.pi_bundle1,
			EventUpgradeIds.pi_bundle2,
			EventUpgradeIds.pi_bundle3,
			EventUpgradeIds.pi_bundle4,
		]),
	getSkin: (type: CollectableType): TileLabelSkin =>
		type === CollectableType.RARE
			? TileLabelSkin.PIRATES_TREASURE
			: TileLabelSkin.PIRATES_MAP,
};

const configs: Record<CollectableEventCategory, EventCollectableConfig> = {
	[EventCategory.EASTER]: easterConfig,
	[EventCategory.HALLOWEEN]: halloweenConfig,
	[EventCategory.PIRATES]: piratesConfig,
};

export function getEventCollectableConfig(
	eventType: CollectableEventCategory,
): EventCollectableConfig {
	return configs[eventType];
}

export function isCollectableEvent(
	eventType: EventCategory | null,
): eventType is CollectableEventCategory {
	return eventType !== null && eventType in configs;
}
